import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { GroupService, InvitationRead, InvitationService } from '../../../../../js/api';
import { groupQueryKeys } from '../../../../../js/app/query-keys-factories/groups';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { setFormErrorsFromApiErrors } from '../../../../../js/utils/forms';
import { BrandSwitch } from '../../../components/BrandSwitch';
import { Button } from '../../../components/button';
import { Dialog, DialogBody, DialogTitle, DialogActions } from '../../../components/dialog';
import { ORGANIZATION_PENDING_INVITATIONS_LIST_QUERY_KEY } from './queryKeys';

interface EditInvitationPermissionsDialogProps {
  invitation: InvitationRead;
  open: boolean;
  onClose: () => void;
}

export function EditInvitationPermissionsDialog({
  invitation,
  open,
  onClose,
}: EditInvitationPermissionsDialogProps) {
  const form = useForm<{ group_names: string[] | boolean }>({
    defaultValues: { group_names: [] as string[] },
  });

  const permissionGroups = useQuery({
    queryKey: groupQueryKeys.list({ limit: 500 }),
    queryFn: async () => {
      const data = await GroupService.groupList({ limit: 500 });
      return data.results || [];
    },
    keepPreviousData: true,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    const groupNames = invitation.group_names;
    if (!groupNames?.length || (groupNames.length === 1 && groupNames[0] === null)) {
      form.setValue('group_names', []);
    } else {
      form.setValue('group_names', groupNames.filter(Boolean));
    }
  }, [open, invitation.group_names, form]);

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (data: { group_names: string[] }) => {
      await InvitationService.invitationPartialUpdate({
        id: invitation.id,
        requestBody: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ORGANIZATION_PENDING_INVITATIONS_LIST_QUERY_KEY],
      });
    },
  });

  const onSubmit = async (data: { group_names: string[] | boolean }) => {
    let submitData: { group_names: string[] };
    if (typeof data.group_names === 'boolean') {
      submitData = { group_names: [] };
    } else if (typeof data.group_names === 'string') {
      submitData = { group_names: [data.group_names] };
    } else {
      submitData = { group_names: data.group_names || [] };
    }

    try {
      await mutation.mutateAsync(submitData);
      toast.success('Invitation permissions updated successfully.');
      onClose();
    } catch (errors) {
      setFormErrorsFromApiErrors(
        errors as Parameters<typeof setFormErrorsFromApiErrors>[0],
        form.setError
      );
      handleApiError(errors);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="4xl">
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <DialogTitle>Edit Invited User Permissions</DialogTitle>
        <DialogBody>
          <div className="tw:space-y-6">
            <p className="tw:text-sm">
              Set the appropriate permissions for <strong>{invitation.email}</strong>
            </p>

            {permissionGroups.isLoading && (
              <div className="tw:flex tw:items-center tw:justify-center tw:py-8">
                <div
                  className="tw:h-8 tw:w-8 tw:animate-spin tw:rounded-full tw:border-2 tw:border-brand-green-0 tw:border-t-transparent"
                  aria-label="loading indicator"
                />
              </div>
            )}

            {permissionGroups.isError && (
              <div className="tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:px-4 tw:py-3 tw:text-red-700 tw:dark:border-red-800 tw:dark:bg-red-950/30 tw:dark:text-red-400">
                <p className="tw:font-semibold">Something went wrong</p>
                <p className="tw:text-sm">We couldn&apos;t retrieve the list of permissions.</p>
              </div>
            )}

            {permissionGroups.isSuccess && permissionGroups.data && (
              <Controller
                name="group_names"
                control={form.control}
                render={({ field }) => (
                  <div className="tw:space-y-4">
                    {permissionGroups.data!.map((group) => {
                      const selected = Array.isArray(field.value)
                        ? field.value.includes(group.code)
                        : false;
                      return (
                        <div key={group.code} className="tw:flex tw:gap-4 tw:items-start">
                          <BrandSwitch
                            className="tw:!mt-1"
                            checked={selected}
                            onChange={(checked) => {
                              const current = Array.isArray(field.value) ? field.value : [];
                              if (checked) {
                                field.onChange([...current, group.code]);
                              } else {
                                field.onChange(current.filter((c) => c !== group.code));
                              }
                            }}
                          />
                          <div className="tw:flex tw:flex-col tw:gap-0.5 tw:min-w-0">
                            <span className="tw:font-medium">{group.title}</span>
                            {group.description && (
                              <span className="tw:text-xs tw:text-zinc-500">
                                {group.description}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              />
            )}
          </div>
        </DialogBody>
        <DialogActions>
          <Button type="button" plain onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            color="brandgreen1"
            disabled={mutation.isPending || permissionGroups.isLoading}
          >
            {mutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
